import {setCityDirection} from '../i18n';
import { appendPlanBackground, animatePlanImage } from "./PlanVisuals";
import { CityApi } from "../api/client";
import type {
  FloorPlanData,
  HouseData,
  PlanAssetData,
  StorefrontPlansResponse,
} from "../api/types";
import {
  appendPlanAssetLights,
  planAssetImageTransform,
  planAssetZIndex,
  runtimePlanAsset,
} from "./PlanAssetLights";

export class StorefrontTour {
  private readonly root = document.createElement("div");
  private readonly stage = document.createElement("div");
  private readonly title = document.createElement("strong");
  private readonly description = document.createElement("p");
  private readonly nav = document.createElement("div");
  private index = 0;
  private plans: FloorPlanData[] = [];
  private assets = new Map<number, PlanAssetData>();
  private businessName = "";

  constructor(private readonly api: CityApi) {
    this.root.className = "city-tour";setCityDirection(this.root);
    this.root.hidden = true;

    const card = document.createElement("div");
    card.className = "city-tour__card";

    const header = document.createElement("div");
    header.className = "city-tour__header";

    const heading = document.createElement("div");
    this.title.className = "city-tour__title";
    heading.appendChild(this.title);

    const close = document.createElement("button");
    close.type = "button";
    close.className = "city-tour__close";
    close.textContent = "×";
    close.setAttribute("aria-label", "بستن");
    close.addEventListener("click", () => this.close());

    header.append(heading, close);

    const body = document.createElement("div");
    body.className = "city-tour__body";
    this.stage.className = "city-tour__stage";
    body.append(this.stage);

    this.description.className = "city-tour__description";
    this.nav.className = "city-tour__nav";

    card.append(header, body, this.description, this.nav);
    this.root.appendChild(card);
    document.body.appendChild(this.root);

    this.root.addEventListener("click", (event) => {
      if (event.target === this.root) this.close();
    });
    window.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !this.root.hidden) this.close();
    });
  }

  async open(house: HouseData): Promise<void> {
    this.businessName = house.businessName;
    this.root.hidden = false;
    document.body.classList.add("city-modal-open");
    this.stage.innerHTML = '<p class="city-empty">در حال ورود به مغازه…</p>';
    try {
      const response: StorefrontPlansResponse =
        await this.api.storefrontPlans(house.externalId);
      this.plans = response.plans
        .filter((plan) => Boolean(plan.id))
        .sort((a, b) => a.sortOrder - b.sortOrder);
      this.assets = new Map(
        response.planAssets.map((asset) => [asset.id, asset]),
      );
      if (!this.plans.length) {
        this.stage.innerHTML =
          '<p class="city-empty">برای این مغازه هنوز نمای داخلی تعریف نشده است.</p>';
        return;
      }
      const facade = this.plans.findIndex((plan) => plan.type === "facade");
      this.index = facade >= 0 ? facade : 0;
      this.render();
    } catch (error) {
      this.stage.innerHTML =
        `<p class="city-empty">${error instanceof Error
          ? error.message
          : "بارگذاری فضای مغازه ناموفق بود."}</p>`;
    }
  }

  close(): void {
    this.root.hidden = true;
    document.body.classList.remove("city-modal-open");
  }

  destroy(): void {
    this.root.remove();
  }

  private render(): void {
    const plan = this.plans[this.index];
    if (!plan) return;

    this.title.textContent = `${this.businessName} · ${plan.title}`;
    this.description.textContent = plan.description || plan.typeLabel || "";
    this.stage.replaceChildren();

    const frame = document.createElement("div");
    frame.className = "city-tour__frame";
    frame.style.backgroundColor=plan.design?.backgroundColor||plan.backgroundColor||"#f1f5f9";

    appendPlanBackground(frame, plan, "city-tour__image");

    for (const component of plan.design?.components ?? []) {
      const asset = this.assets.get(component.assetId);
      if (!asset?.imageUrl) continue;
      appendPlanAssetLights(frame, asset, component, 20);
      const overlay = document.createElement("img");
      overlay.className = "city-tour__asset";
      overlay.src = asset.imageUrl;
      animatePlanImage(overlay, runtimePlanAsset(asset));
      overlay.alt = "";
      overlay.style.left = `${component.x}%`;
      overlay.style.top = `${component.y}%`;
      overlay.style.zIndex = String(planAssetZIndex(asset, component, 20));
      overlay.style.transform = planAssetImageTransform(asset, component);
      overlay.style.transformOrigin = `${(runtimePlanAsset(asset).anchorX ?? 0.5) * 100}% ${(runtimePlanAsset(asset).anchorY ?? 0.5) * 100}%`;
      frame.appendChild(overlay);
    }

    if (plan.type === "facade" && plan.doorHotspot) {
      const door = document.createElement("button");
      door.type = "button";
      door.className = "city-tour__door";
      door.style.left = `${plan.doorHotspot.x}%`;
      door.style.top = `${plan.doorHotspot.y}%`;
      door.style.width = `${plan.doorHotspot.width}%`;
      door.style.height = `${plan.doorHotspot.height}%`;
      door.title = "ورود به مغازه";
      door.setAttribute("aria-label", "ورود به مغازه");
      door.addEventListener("click", () => {
        const entry = this.plans.findIndex((item) => item.type === "entry");
        const interior = this.plans.findIndex((item) => item.type !== "facade");
        const target = entry >= 0 ? entry : interior;
        if (target >= 0) {
          this.index = target;
          this.render();
        }
      });
      frame.appendChild(door);
    }

    this.stage.appendChild(frame);
    this.renderNav();
  }

  private renderNav(): void {
    this.nav.replaceChildren();
    this.plans.forEach((item, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = index === this.index
        ? "city-tour__nav-btn is-active"
        : "city-tour__nav-btn";
      button.textContent = item.typeLabel || item.title;
      button.addEventListener("click", () => {
        this.index = index;
        this.render();
      });
      this.nav.appendChild(button);
    });
  }


}
